import React from 'react';
import { Button } from '../ui/Button';
import { CheckCircle, Download, FileText, BarChart } from 'lucide-react';
import { ReportAsset } from '../../types';
import { ProfilesService } from '../../services/profilesService';

interface AssetDownloadGridProps {
  assets: ReportAsset[];
  className?: string;
}

export const AssetDownloadGrid: React.FC<AssetDownloadGridProps> = ({ 
  assets, 
  className 
}) => {
  const handleDownloadAsset = async (asset: ReportAsset) => {
    try {
      // Make authenticated request to download the actual file content
      const response = await ProfilesService.downloadAssetContent(asset.id)
      
      if (response.ok) {
        // Get the file blob from the response
        const blob = await response.blob()
        
        // Create object URL and trigger download
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = asset.file_name || `asset-${asset.id}.${asset.asset_type}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Clean up the object URL
        window.URL.revokeObjectURL(url)
        
        console.log(`✅ Successfully downloaded asset: ${asset.file_name}`)
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Download failed' }))
        console.error('Download failed:', errorData)
        alert(`Download failed: ${errorData.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error downloading asset:', error)
      alert(`Download error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const getAssetIcon = (assetType: string) => {
    switch (assetType) {
      case 'pdf':
        return FileText
      case 'markdown':
        return FileText
      case 'json':
        return BarChart
      case 'chart':
        return BarChart
      default:
        return FileText
    }
  }

  const getAssetLabel = (asset: ReportAsset) => {
    // Use asset_subtype for more specific labels
    if (asset.asset_subtype === 'final-report') return 'PDF Report'
    if (asset.asset_subtype === 'processed-source') return 'Markdown Source'
    if (asset.asset_subtype === 'metadata') return 'Report Metadata'

    // Fallback to generic labels based on asset_type
    switch (asset.asset_type) {
      case 'pdf':
        return 'PDF Report'
      case 'markdown':
        return 'Markdown'
      case 'json':
        return 'Metadata'
      case 'html':
        return 'HTML Report'
      case 'chart':
        return 'Chart'
      default:
        return `${asset.asset_type.toUpperCase()} Report`
    }
  }

  if (!assets || assets.length === 0) {
    return null;
  }

  return (
    <div className={`p-3 bg-green-50 rounded-lg border-l-4 border-l-green-400 ${className || ''}`}>
      <h4 className="text-sm font-semibold text-green-900 mb-3 flex items-center">
        <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
        Analysis Reports Ready ({assets.length})
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {assets.map((asset) => {
          const IconComponent = getAssetIcon(asset.asset_type)
          return (
            <Button
              key={asset.id}
              onClick={() => handleDownloadAsset(asset)}
              size="sm"
              variant="outline"
              className="flex items-center justify-between text-xs bg-white hover:bg-green-50 border-green-200 hover:border-green-300 p-3 h-auto"
            >
              <div className="flex items-center">
                <IconComponent className="h-4 w-4 mr-2 text-green-600" />
                <span className="font-medium">
                  {getAssetLabel(asset)}
                </span>
              </div>
              <Download className="h-3 w-3 text-green-600" />
            </Button>
          )
        })}
      </div>
    </div>
  );
};